/**
 * QorTrace Labs · hub hero · pure-CSS TLS-handshake terminal animation.
 *
 * Replaces the Playwright-rendered hero.mp4 that ships from the
 * backend.  In production the server-rendered MP4 endpoint returns
 * `503: No module named 'playwright'` because the deployed runtime
 * does not carry the Playwright + Chromium binaries used to bake the
 * clip locally; the frontend `<video>` tag silently fails on the JSON
 * 503 and the hero looked frozen.
 *
 * This component reproduces the terminal aesthetic with pure CSS
 * (scrolling trace lines + animated handshake-status pulse) so the
 * hero animates everywhere — preview, prod, fork, anywhere — without
 * requiring any server-side rendering pipeline.
 *
 * iter148 — "Read the trace" toggle:
 *   Below the hero, the caption row exposes a single toggle button.
 *   When activated, the scrolling animation pauses and selected
 *   lines reveal a one-line annotation explaining what is
 *   post-quantum-safe about that token (key_share hybrid construction,
 *   FIPS-204 signature algos, HKDF-SHA384 traffic-key derivation,
 *   etc).  The annotations are pure HTML — no JS DOM thrash beyond
 *   the toggle's class flip — so the read-mode is GPU-cheap.
 *
 * Accessibility:
 *   - In play-mode the visual is `aria-hidden="true"` (decorative).
 *   - In read-mode it becomes a real region with `role="region"` and
 *     an aria-label so screen-reader users can navigate annotations.
 *   - The toggle button is a native <button> with aria-pressed.
 */
import React, { useState } from "react";
import { PlayCircle, PauseCircle } from "lucide-react";
import { trackEvent } from "../../lib/analytics";


// ─────────────────────────────────────────────────────────────────────── ##
// Trace lines.  Identical to the cycle the Playwright pipeline
// captures, but each line can carry an optional `note` string — a
// one-line annotation surfaced ONLY in read-mode.  Notes are 1-2
// sentences max so they never expand the panel beyond the card.
// ─────────────────────────────────────────────────────────────────────── ##
const TRACE = [
  { gut: "01", cls: "cmd",  body: <><span className="prompt">$</span>openssl s_client -connect <span className="flag">api.bank-tier1.com</span>:443 -groups <span className="flag">X25519MLKEM768</span></>,
    note: "We pin the hybrid group at connect time so the server cannot silently downgrade to classical X25519." },
  { gut: "02", cls: "dim",  body: "CONNECTED(00000003)" },
  { gut: "03", cls: "dim",  body: "depth=2  /C=US/O=DigiCert Inc/CN=DigiCert Global Root G2" },
  { gut: "04", cls: "dim",  body: "verify return:1" },
  { gut: "05", cls: "note", body: "── ClientHello ──────────────────────────" },
  { gut: "06", cls: "ok",   body: "› key_share          X25519MLKEM768   (hybrid)",
    note: "Hybrid key share = classical X25519 ⊕ post-quantum ML-KEM-768. An attacker must break BOTH to recover the secret." },
  { gut: "07", cls: "ok",   body: "› supported_groups   x25519, x448, secp256r1, ML-KEM-768",
    note: "Advertises PQ-capable groups so PQ-aware peers can negotiate; classical-only servers still complete via X25519." },
  { gut: "08", cls: "ok",   body: "› signature_algos    ML-DSA-65, ECDSA-P256, RSA-PSS-SHA256",
    note: "ML-DSA-65 (FIPS 204) is the post-quantum signature scheme — Shor-resistant, ~256-bit security level." },
  { gut: "09", cls: "note", body: "── ServerHello ──────────────────────────" },
  { gut: "10", cls: "k",    body: <>‹ <b>X25519MLKEM768</b> selected · 1184-byte ML-KEM ciphertext</>,
    note: "Server selected the hybrid group. 1184 B is the ML-KEM-768 ciphertext that wraps the PQ key share." },
  { gut: "11", cls: "hex",  body: <>‹ shared_secret = <b>0x7af9d2…b0e3d5</b> (32 B · derived)</>,
    note: "Final secret derived from BOTH the X25519 and ML-KEM shared secrets via SHA-384 — PQ-secure by construction." },
  { gut: "12", cls: "ok",   body: "‹ Cipher: TLS_AES_256_GCM_SHA384 · 0-RTT off",
    note: "AES-256 is Grover-resistant at the 128-bit effective level. 0-RTT is disabled to prevent replay against PQ state." },
  { gut: "13", cls: "ok",   body: "‹ HKDF-SHA384 expand → traffic keys",
    note: "HKDF with SHA-384 is quantum-safe; traffic keys are derived from the PQ-protected master secret." },
  { gut: "14", cls: "note", body: "── Verifying server certificate ─────────" },
  { gut: "15", cls: "dim",  body: "issuer  : DigiCert Global G3 ECC SHA384 2020 CA1" },
  { gut: "16", cls: "dim",  body: "subject : api.bank-tier1.com · ML-DSA-65",
    note: "Endpoint certificate is signed with ML-DSA-65 — FIPS 204 post-quantum digital signature." },
  { gut: "17", cls: "ok",   body: "verify_result: 0 (Verify OK · post-quantum signed)",
    note: "Identity binding proven against a PQ signature chain; valid even after a CRQC arrives." },
  { gut: "18", cls: "banner", body: "▶ HANDSHAKE COMPLETE · X25519+ML-KEM-768 · 312 ms",
    note: "Hybrid handshake added ~80 ms vs classical X25519 — well under the user-perceptible threshold (~250 ms)." },
  { gut: "19", cls: "note", body: "── Application data ─────────────────────" },
  { gut: "20", cls: "dim",  body: <>GET /v1/payments/intents/<span style={{ color: "var(--labs-cyan)" }}>pi_3xQv4kK</span> HTTP/1.1</> },
  { gut: "21", cls: "ok",   body: "200 OK · 1.2 KB · keep-alive · pq-secure" },
];


function TraceBlock({ readMode }) {
  // In play-mode we render the lines twice so the keyframe can
  // translate by exactly -50% for a seamless loop. In read-mode we
  // render once (no duplicate, no animation) so the user can read
  // every annotation without it scrolling under their cursor.
  const lines = TRACE.map((ln, i) => (
    <div key={`a-${i}`} className={`labs-trace-ln ${ln.cls}${ln.note ? " has-note" : ""}`}>
      <span className="gut">{ln.gut}</span>
      <span className="body">{ln.body}</span>
      {readMode && ln.note && (
        <div className="labs-trace-note" data-testid={`labs-trace-note-${ln.gut}`}>
          {ln.note}
        </div>
      )}
    </div>
  ));
  if (readMode) return <>{lines}</>;
  return (
    <>
      {lines}
      {TRACE.map((ln, i) => (
        <div key={`b-${i}`} className={`labs-trace-ln ${ln.cls}`} aria-hidden="true">
          <span className="gut">{ln.gut}</span>
          <span className="body">{ln.body}</span>
        </div>
      ))}
    </>
  );
}


export default function LabsHubHero({ readMode = false }) {
  return (
    <div className={`labs-hub-hero-term${readMode ? " is-read-mode" : ""}`}
         aria-hidden={readMode ? undefined : "true"}
         role={readMode ? "region" : undefined}
         aria-label={readMode ? "TLS 1.3 hybrid post-quantum handshake — annotated read view" : undefined}
         data-testid="labs-hub-hero-term">
      {/* Macos-style chrome */}
      <div className="labs-hub-hero-bar">
        <span className="labs-hub-hero-dot r" />
        <span className="labs-hub-hero-dot y" />
        <span className="labs-hub-hero-dot g" />
        <div className="labs-hub-hero-title">
          <b>tls-trace</b> &middot; openssl s_client &middot; <span>x25519+ml-kem-768</span>
        </div>
        <div className="labs-hub-hero-pid">PID 18432</div>
      </div>

      {/* Body — left scrolling trace + right key-share panel */}
      <div className="labs-hub-hero-body">
        <div className="labs-hub-hero-pane labs-hub-hero-pane-left">
          <div className="labs-hub-hero-trace-wrap">
            <div className="labs-hub-hero-trace">
              <TraceBlock readMode={readMode} />
            </div>
          </div>
          {!readMode && <div className="labs-hub-hero-fade" />}
        </div>

        <div className="labs-hub-hero-pane labs-hub-hero-pane-right">
          <div className="labs-hub-hero-ks-title">
            LIVE HANDSHAKE
          </div>

          <div className="labs-hub-hero-ks-card hybrid"
               data-testid="labs-hub-hero-ks-hybrid">
            <div className="lbl">KEY EXCHANGE</div>
            <div className="alg">X25519 + ML-KEM-768</div>
            <div className="meta">
              <span>FIPS 203</span>
              <span>Hybrid</span>
              <span>Level 3</span>
              <span>~256-bit</span>
            </div>
            {readMode && (
              <div className="labs-trace-note in-card" data-testid="labs-ks-note-hybrid">
                Pairs classical X25519 with FIPS&nbsp;203 ML-KEM-768.
                Defender wins if EITHER scheme holds; attacker must
                break BOTH.
              </div>
            )}
          </div>

          <div className="labs-hub-hero-ks-card sig"
               data-testid="labs-hub-hero-ks-sig">
            <div className="lbl">SIGNATURE</div>
            <div className="alg">ML-DSA-65</div>
            <div className="meta">
              <span>FIPS 204</span>
              <span>2.4 KB</span>
              <span>Verified</span>
              <span>PQ-safe</span>
            </div>
            {readMode && (
              <div className="labs-trace-note in-card" data-testid="labs-ks-note-sig">
                FIPS&nbsp;204 lattice-based signature. Replaces RSA-PSS
                / ECDSA on the certificate without changing the
                operational PKI flow.
              </div>
            )}
          </div>

          <div className="labs-hub-hero-status">
            <span className="labs-hub-hero-status-dot" />
            HANDSHAKE OK · 312 ms
          </div>
        </div>
      </div>
    </div>
  );
}


/** Caption row + Read-the-trace toggle.
 *  Renders the framed terminal card AND the caption + toggle below
 *  it. This is consumed by `<Labs.jsx>` as a single unit so the read-
 *  mode flag is co-located with the toggle that flips it (no prop
 *  drilling through the parent grid).
 */
export function LabsHubHeroCaption() {
  const [readMode, setReadMode] = useState(false);
  // Track each toggle event + dwell-time the user spent in read-mode.
  // `hero_read_toggle_opened` fires on the OPEN click; the matching
  // `hero_read_toggle_closed` event carries `dwell_ms` so PostHog can
  // segment by "actually read for >= 30s" vs "bounced in <5s" — the
  // exact thesis we want to validate before doubling down on inline
  // explainers elsewhere on the site.
  const [openedAt, setOpenedAt] = React.useState(null);
  const onToggle = () => {
    setReadMode((v) => {
      const next = !v;
      if (next) {
        const t = Date.now();
        setOpenedAt(t);
        trackEvent("hero_read_toggle_opened", { ts: t });
      } else {
        const t = Date.now();
        trackEvent("hero_read_toggle_closed", {
          ts: t,
          dwell_ms: openedAt ? t - openedAt : null,
        });
        setOpenedAt(null);
      }
      return next;
    });
  };
  return (
    <>
      <div className="labs-hero-video-frame">
        <LabsHubHero readMode={readMode} />
      </div>
      <div className="labs-hero-video-caption">
        <span className={`dot-mini${readMode ? " is-paused" : ""}`} />
        {readMode
          ? <>Annotated · TLS 1.3 hybrid <span style={{ color: "var(--labs-cyan)" }}>X25519+ML-KEM-768</span> · paused</>
          : <>Live trace · TLS 1.3 · classical&nbsp;ECDHE → hybrid&nbsp;X25519+ML-KEM-768</>}
        <button type="button"
                className="labs-hero-read-toggle"
                data-testid="labs-hero-read-toggle"
                aria-pressed={readMode}
                onClick={onToggle}>
          {readMode ? <><PlayCircle size={12} /> Resume scroll</>
                    : <><PauseCircle size={12} /> Read the trace</>}
        </button>
      </div>
    </>
  );
}
